import React from 'react';

const RARITY_TO_COLORS = {
  common:    ['#00f0ff', '#0066ff', '#7a00ff'],
  uncommon:  ['#ccff00', '#00f0ff', '#00ffc8'],
  rare:      ['#00f0ff', '#7a00ff', '#ff007f'],
  epic:      ['#ff007f', '#7a00ff', '#00f0ff'],
  legendary: ['#ffd700', '#ff5f00', '#ff007f'],
  mythic:    ['#00ffc8', '#ff007f', '#ffd700'],
};

const SIZE_MAP = {
  sm: 'h-32 w-32',
  md: 'h-60 w-60',
  lg: 'h-80 w-80',
  xl: 'h-[26rem] w-[26rem]',
};

/**
 * Pure-CSS iridescent 3D bubble. No WebGL dependency.
 * Uses layered conic + radial gradients, breathing animation, and inner highlights.
 */
const BubbleOrb = ({ rarity = 'common', size = 'md', glow = true }) => {
  const colors = RARITY_TO_COLORS[rarity] || RARITY_TO_COLORS.common;
  const klass = SIZE_MAP[size] || SIZE_MAP.md;
  const id = `b-${rarity}-${size}`;

  return (
    <div className={`relative ${klass}`} data-testid={`bubble-orb-${rarity}`}>
      {glow && (
        <div
          className="absolute inset-0 rounded-full blur-3xl opacity-60 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 35% 30%, ${colors[0]} 0%, ${colors[1]} 45%, transparent 75%)`,
          }}
        />
      )}
      {/* Outer rim with conic shimmer */}
      <div
        className="absolute inset-0 rounded-full breathe spin-slow"
        style={{
          background: `conic-gradient(from 0deg, ${colors[0]}, ${colors[1]}, ${colors[2]}, ${colors[0]})`,
          filter: 'blur(4px)',
          opacity: 0.85,
        }}
      />
      {/* Bubble body */}
      <div
        className="absolute inset-[6%] rounded-full breathe overflow-hidden"
        style={{
          background: `
            radial-gradient(circle at 30% 25%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.4) 8%, transparent 30%),
            radial-gradient(circle at 70% 75%, ${colors[2]} 0%, transparent 50%),
            radial-gradient(circle at 35% 40%, ${colors[0]} 0%, ${colors[1]} 60%, ${colors[2]} 100%)
          `,
          boxShadow: `
            inset 0 0 60px rgba(255,255,255,0.18),
            inset 0 0 100px rgba(0,0,0,0.45),
            0 0 60px ${colors[0]}40
          `,
        }}
      >
        {/* iridescent overlay */}
        <div
          className="absolute inset-0 rounded-full mix-blend-screen opacity-50"
          style={{
            background: `conic-gradient(from 45deg, transparent 0deg, ${colors[0]}55 90deg, transparent 180deg, ${colors[2]}55 270deg, transparent 360deg)`,
          }}
        />
        {/* highlight */}
        <div
          className="absolute top-[10%] left-[18%] h-[28%] w-[28%] rounded-full blur-md"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.85), transparent 70%)' }}
        />
        <div
          className="absolute top-[20%] left-[28%] h-[6%] w-[6%] rounded-full"
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      </div>
      {/* sparkle particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              top: `${10 + ((i * 37) % 80)}%`,
              left: `${10 + ((i * 53) % 80)}%`,
              width: 2 + (i % 3),
              height: 2 + (i % 3),
              opacity: 0.55,
              animation: `float ${3 + i * 0.5}s ease-in-out ${i * 0.3}s infinite`,
              boxShadow: `0 0 8px ${colors[i % colors.length]}`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default BubbleOrb;
